import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import Portal from "./Portal";
import { twMerge } from "tailwind-merge";

enum TooltipVariants {
    Black = "black",
    Stone = "stone"
}

const TooltipStyles: Record<TooltipVariants, string> = {
    [TooltipVariants.Black]: "bg-black text-white",
    [TooltipVariants.Stone]: "border border-stone-200 bg-white text-stone-600 shadow-xl"
};

// Long enough to cross the offset between trigger and tooltip at a normal pointer speed.
const HOVER_GRACE_MS = 250;

interface TooltipProps {
    text?: string;
    variant?: string;
    active?: boolean;
    verticalOffset?: number;
    horizontalOffset?: number;
    children: JSX.Element | ReactNode;
    className?: string;
    childrenClassName?: string;
    align?: "bottom" | "bottom start" | "bottom end" | "top" | "top start" | "top end" | "left" | "right";

    // Delay in milliseconds before showing the tooltip after hover
    showDelay?: number;
    content?: ReactNode | string;

    // Keeps the tooltip open while the pointer is over it, for content the reader acts on.
    interactive?: boolean;
}

/**
 * Tooltip component renders a tooltip near its child element using a Portal.
 * The tooltip can be aligned to any side of the trigger using the 'align' prop.
 * The position can be further adjusted using 'verticalOffset' and 'horizontalOffset' props.
 */
const Tooltip = ({
    content,
    text, // Deprecated: use content instead
    variant = TooltipVariants.Black,
    children,
    className,
    childrenClassName,
    verticalOffset = 0,
    horizontalOffset = 0,
    active = true,
    align = "bottom",
    showDelay = 300,
    interactive = false
}: TooltipProps) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const toolTipRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const displayContent = content || text;

    /**
     * Calculates the tooltip position based on the chosen alignment.
     * Supports start/end alignment for top/bottom positions.
     * Prevents tooltip from going off-screen.
     */
    useEffect(() => {
        if (show && triggerRef.current && toolTipRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const toolTipRect = toolTipRef.current.getBoundingClientRect();

            let newLeft = 0,
                newTop = 0;
            switch (align) {
                case "left":
                    // Position tooltip to the left of the trigger, vertically centered
                    newLeft = triggerRect.left - toolTipRect.width + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + (triggerRect.height - toolTipRect.height) / 2 + verticalOffset + window.scrollY;
                    if (newLeft < 0) newLeft = window.scrollX;
                    break;
                case "right":
                    // Position tooltip to the right of the trigger, vertically centered
                    newLeft = triggerRect.right + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + (triggerRect.height - toolTipRect.height) / 2 + verticalOffset + window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                case "top":
                    // Position tooltip above the trigger, horizontally centered
                    newLeft = triggerRect.left + (triggerRect.width - toolTipRect.width) / 2 + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top - toolTipRect.height + verticalOffset + window.scrollY;
                    if (newTop < 0) newTop = window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                case "top start":
                    // Position tooltip above the trigger, left-aligned
                    newLeft = triggerRect.left + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top - toolTipRect.height + verticalOffset + window.scrollY;
                    if (newTop < 0) newTop = window.scrollY;
                    break;
                case "top end":
                    // Position tooltip above the trigger, right-aligned
                    newLeft = triggerRect.right - toolTipRect.width + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top - toolTipRect.height + verticalOffset + window.scrollY;
                    if (newTop < 0) newTop = window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                case "bottom":
                    // Position tooltip below the trigger, horizontally centered
                    newLeft = triggerRect.left + (triggerRect.width - toolTipRect.width) / 2 + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + triggerRect.height + verticalOffset + window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                case "bottom start":
                    // Position tooltip below the trigger, left-aligned
                    newLeft = triggerRect.left + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + triggerRect.height + verticalOffset + window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                case "bottom end":
                    // Position tooltip below the trigger, right-aligned
                    newLeft = triggerRect.right - toolTipRect.width + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + triggerRect.height + verticalOffset + window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
                default:
                    // Default to 'bottom start' (left-aligned below)
                    newLeft = triggerRect.left + horizontalOffset + window.scrollX;
                    newTop = triggerRect.top + triggerRect.height + verticalOffset + window.scrollY;
                    if (newLeft + toolTipRect.width > window.innerWidth) newLeft = window.innerWidth - toolTipRect.width + window.scrollX;
                    break;
            }

            // Check to make sure that the tooltip is not positioned off-screen horizontally
            // The -2 offset is to add a small margin from the edge of the viewport
            if (newLeft < 0) newLeft = window.scrollX - 2;

            // Check to make sure that the tooltip is not positioned off-screen vertically
            if (newTop < 0) newTop = window.scrollY;

            setPosition({ top: newTop, left: newLeft });
        } else if (!show) {
            setPosition(null);
        }
    }, [show, displayContent, verticalOffset, horizontalOffset, align]);

    // Hide tooltip if clicking or moving mouse anywhere outside the trigger
    useEffect(() => {
        if (!show) return;
        function handleDocumentClick(e: MouseEvent) {
            const insideTrigger = triggerRef.current?.contains(e.target as Node);
            // An interactive tooltip's own controls live in the portal, not the trigger, so
            // closing on any outside mousedown would kill the click before it landed.
            const insideTooltip = interactive && toolTipRef.current?.contains(e.target as Node);
            if (triggerRef.current && !insideTrigger && !insideTooltip) {
                setShow(false);
            }
        }
        function handleDocumentMouseMove(e: MouseEvent) {
            const isOverTrigger = triggerRef.current?.contains(e.target as Node);
            const isOverTooltip = toolTipRef.current?.contains(e.target as Node);
            if (isOverTrigger || isOverTooltip) {
                if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                    hideTimeoutRef.current = null;
                }
                return;
            }
            // Interactive tooltips get the same grace period here as on mouseleave —
            // otherwise the first move into the gap between the two closes it.
            if (interactive) {
                if (hideTimeoutRef.current === null) {
                    hideTimeoutRef.current = setTimeout(() => setShow(false), HOVER_GRACE_MS);
                }
                return;
            }
            setShow(false);
        }

        // This will trigger on any scroll event in the document, not just the body scroll.
        function handleScroll() {
            setShow(false);
        }
        document.addEventListener("mousedown", handleDocumentClick);
        document.addEventListener("mousemove", handleDocumentMouseMove);
        window.addEventListener("scroll", handleScroll, true);
        return () => {
            document.removeEventListener("mousedown", handleDocumentClick);
            document.removeEventListener("mousemove", handleDocumentMouseMove);
            window.removeEventListener("scroll", handleScroll, true);
        };
    }, [show, interactive]);

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (!active) return;
        if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
        keepOpen();

        // If showDelay is set, use it to delay showing the tooltip
        if (showDelay > 0) {
            showTimeoutRef.current = setTimeout(() => {
                setShow(true);
            }, showDelay);
        } else {
            setShow(true);
        }
    };

    const handleMouseLeave = () => {
        // Clear the show timeout if it exists
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }

        // Grace period to cross the gap between trigger and tooltip.
        if (interactive) {
            hideTimeoutRef.current = setTimeout(() => setShow(false), HOVER_GRACE_MS);
            return;
        }
        setShow(false);
    };

    const keepOpen = () => {
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const colorStyle = TooltipStyles[variant];

    return (
        <>
            <div ref={triggerRef} className={twMerge("relative", childrenClassName)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {children}
            </div>
            {show && displayContent && (
                <Portal>
                    <div
                        ref={toolTipRef}
                        className={twMerge(
                            colorStyle,
                            "absolute z-[100] rounded-lg px-2 py-1 text-sm shadow-2xl",
                            "hidden sm:block", // Hide on mobile, show on >=sm
                            className
                        )}
                        // Initial render is parked off-screen so the absolute element can't
                        // extend the document and trigger a scrollbar/reflow flicker before
                        // the useEffect computes its real position.
                        style={position ? { ...position } : { top: -9999, left: -9999, visibility: "hidden" as const }}
                        onMouseEnter={interactive ? keepOpen : undefined}
                        onMouseLeave={interactive ? handleMouseLeave : undefined}
                    >
                        {displayContent}
                    </div>
                </Portal>
            )}
        </>
    );
};

export default Tooltip;
